"use client";

import React from "react";
import { Microscope, HardHat, ArrowRight, Building2, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function AcademicPracticalFoundation() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="nền-tảng-học-thuật-thực-tiễn" className="w-full bg-background relative overflow-hidden border-t border-border" ref={sectionRef}>
      <div className="grid lg:grid-cols-5 min-h-[700px]">
        {}
        <div className="lg:col-span-3 relative h-[400px] lg:h-auto overflow-hidden group">
          {}
          <div className="absolute inset-0 bg-primary/10 z-10 mix-blend-multiply" />
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1569116/img/nuce_lab_research_blueprint.png" alt="National University of Civil Engineering Laboratory" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          
          {}
          <div className="absolute inset-0 z-20 opacity-30 pointer-events-none" style={{
          backgroundImage: `
                linear-gradient(to right, var(--primary) 1px, transparent 1px),
                linear-gradient(to bottom, var(--primary) 1px, transparent 1px)
              `,
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(circle at center, black 40%, transparent 100%)'
        }} />
          
          {}
          <div className="absolute bottom-8 left-8 z-30 bg-background/90 backdrop-blur-sm p-4 border-l-4 border-accent shadow-lg max-w-xs hidden sm:block">
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="w-5 h-5 text-primary" />
              <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Facility Status</span>
            </div>
            <p className="text-sm font-bold text-primary">Advanced Materials Lab Active</p>
            <div className="mt-2 h-1 w-full bg-muted overflow-hidden">
              <div className="h-full bg-accent w-2/3 animate-pulse" />
            </div>
          </div>
        </div>

        {}
        <div className="lg:col-span-2 flex flex-col justify-center p-8 lg:p-16 xl:p-20 bg-background relative">
          <div className={`transition-all duration-700 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="mb-6">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
                The Nexus of <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-chart-4">Academia & Practice</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Bridging the gap between theoretical research and practical application in the construction industry.
              </p>
            </div>

            <Tabs defaultValue="academic" className="w-full">
              <TabsList className="w-full grid grid-cols-2 mb-8 bg-muted/50 p-1 border border-border/50">
                <TabsTrigger value="academic" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md transition-all duration-300">
                  <GraduationCap className="w-4 h-4 mr-2" />
                  Academic Research
                </TabsTrigger>
                <TabsTrigger value="market" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md transition-all duration-300">
                  <HardHat className="w-4 h-4 mr-2" />
                  Market Application
                </TabsTrigger>
              </TabsList>

              <TabsContent value="academic" className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-primary flex items-center gap-2">
                    <Microscope className="w-5 h-5 text-accent" />
                    Research & Development
                  </h3>
                  <p className="text-muted-foreground">
                    Leveraging the intellectual powerhouse of the National University of Civil Engineering, we conduct cutting-edge research into sustainable materials, structural integrity, and smart city technologies.
                  </p>
                  <ul className="space-y-3 mt-4">
                    {["Advanced Material Testing", "Structural Health Monitoring", "Geotechnical Engineering Analysis"].map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3 text-sm text-primary/80">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                        {item}
                      </li>)}
                  </ul>
                </div>
                <div className="pt-4">
                  <Button asChild className="group bg-primary hover:bg-primary/90 text-white">
                    <Link to="/SolutionsPage">
                      Explore Research
                      <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="market" className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-primary flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-accent" />
                    Technology Transfer
                  </h3>
                  <p className="text-muted-foreground">
                    We transform academic breakthroughs into viable commercial solutions. NUCETECH serves as the vital link that brings laboratory innovations to real-world construction sites.
                  </p>
                  <ul className="space-y-3 mt-4">
                    {["Digital Transformation Consulting", "Construction Project Optimization", "Smart Construction Solutions"].map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3 text-sm text-primary/80">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-chart-4 shrink-0" />
                        {item}
                      </li>)}
                  </ul>
                </div>
                <div className="pt-4">
                  <Button asChild className="group bg-primary hover:bg-primary/90 text-white">
                    <Link to="/SolutionsPage">
                      View Solutions
                      <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </section>;
}